import React from "react";
import {
  MessageSquare, FileText, CreditCard, Send, Eye,
  Zap, DollarSign, Sparkles, Mail, Smartphone, Activity, ShieldCheck,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FlowDiagram, ConversationThread, FeatureCallout, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogTextToPayInstantStatements = () => (
  <RichBlogLayout
    category="Patient Billing"
    eyebrow="Patient Billing"
    title="Text-to-Pay: How Instant Statements Replace 30 Days of Paper Mail"
    subtitle="An itemized statement, a tone-matched message, a Stripe-hosted link. Most balances clear in under 60 seconds — without a single envelope."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      A $90 balance posts to a patient's account on Tuesday morning. In a
      paper-statement practice, that balance enters a 30-day cycle: print,
      stuff, mail, deliver, sit on a counter, get noticed, get paid — maybe.
      In a text-to-pay practice, the same $90 is collected before the patient
      finishes their lunch.
    </Lead>

    <Prose>
      <p>
        Paper statements work against a small dental practice from both
        ends. Each printed statement costs roughly a dollar in paper,
        postage, and envelope handling — sometimes more once staff time is
        included. The cycle takes weeks. Patients receive the statement,
        set it on a pile, and revisit it when they revisit the pile, which
        is sometimes never. And every additional cycle compounds: a second
        statement is another dollar, another delay, another reason for the
        balance to drift into AR aging.
      </p>
      <p>
        Text-to-pay collapses that cycle into a sequence that finishes
        before the patient leaves the parking lot. The moment a claim posts,
        Asha builds an itemized statement, picks a channel, drafts a message
        that matches the patient's tone, lets staff preview, and sends. The
        patient taps a link. They pay. The balance closes.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 60, suffix: "s", label: "Most payments complete in under a minute" },
        { value: 1, prefix: "$", label: "Cost per mailed paper statement (paper + postage)" },
        { value: 30, suffix: "+ days", label: "Traditional statement cycle" },
        { value: 0, label: "Envelopes printed" },
      ]}
    />

    <Prose>
      <h2>Why paper statements were never going to scale</h2>
      <p>
        A practice that mails 200 statements a month is spending roughly
        $200 in raw cost — and an order of magnitude more in staff time
        getting them out the door. The return on that effort isn't great.
        Industry benchmarks suggest a meaningful share of mailed statements
        get partially or fully ignored, and the recoverable balance shrinks
        the longer it sits.
      </p>
      <p>
        The harder cost, though, is the latency. Paper statements teach
        patients that bills are not urgent. By the time they actually
        receive one, the procedure is fading from memory, the insurance
        explanation feels stale, and the balance feels like it came out of
        nowhere. Text-to-pay flips that. The bill arrives while the visit is
        still fresh.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Mailed paper statements"
      afterTitle="Asha text-to-pay"
      beforeItems={[
        "$1+ per statement in paper, postage, and handling",
        "Multi-week cycle from print to received",
        "Patients ignore stacks of envelopes for weeks",
        "Repeat cycles compound cost and AR aging",
      ]}
      afterItems={[
        "Itemized statement built the moment the claim posts",
        "Channel + tone chosen automatically based on patient history",
        "Stripe-hosted link, no login, no paperwork, mobile-first",
        "Most balances cleared in under 60 seconds",
      ]}
    />

    <Prose>
      <h2>What 'instant statement' really means</h2>
      <p>
        It is one thing to send a text faster. It is another to make sure
        the text contains a statement the patient actually understands. A
        line that says "balance: $312.40" without context is a customer
        service call waiting to happen.
      </p>
      <p>
        Asha builds the statement line by line — each procedure, each
        insurance contribution, each adjustment, each remaining balance —
        the moment the claim posts. The patient sees what they were charged
        for, what insurance paid, and what's left, in plain language. There
        are no mystery totals.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Claim posts to the ledger",
          body:
            "Insurance pays its portion. Patient responsibility is calculated against contracted fees and any prior payments.",
        },
        {
          title: "Itemized statement is built automatically",
          body:
            "Every line of work — procedure, code, contracted rate, insurance allowance, patient portion — is rendered in plain language.",
        },
        {
          title: "Channel and tone are chosen from patient history",
          body:
            "Texting? Email? Formal? Casual? Asha pulls past communication patterns to pick the channel and the voice that fits.",
        },
        {
          title: "Staff previews before send",
          body:
            "A quick approval moment. Staff see the message exactly as the patient will, with the link and statement attached.",
        },
        {
          title: "Stripe-hosted payment in under 60 seconds",
          body:
            "The patient taps the link. PCI-compliant, mobile-first, no login. Payment posts back to the chart instantly.",
        },
      ]}
    />

    <PullQuote>
      What used to sit as a $90 balance in AR aging is now resolved almost
      immediately after posting.
    </PullQuote>

    <Prose>
      <h2>What the patient actually sees</h2>
      <p>
        The message itself is the thing. A generic, robotic "you have a
        balance, click here" SMS gets ignored. A message that sounds like
        someone from the practice — one who already knows the patient's
        rhythm — gets opened. Asha matches the tone to the patient because
        the tone is the difference between a paid balance and an ignored
        one.
      </p>
    </Prose>

    <ConversationThread
      title="Maria L · Text-to-pay · 11:47 AM"
      channel="SMS"
      messages={[
        { from: "system", text: "Claim posted · Patient responsibility: $312.40" },
        { from: "agent", text: "Hi Maria — quick note from your dentist's office. Your insurance just settled on yesterday's crown visit. Your portion is $312.40. Here's a secure link to pay if it's a good time: pay.asha.dental/m9k4 — itemized breakdown is on the page. Thanks!" },
        { from: "patient", text: "Just paid, thanks for breaking it down so clearly." },
        { from: "system", text: "Payment received · Ledger updated · Receipt sent · Time to resolution: 47 seconds" },
      ]}
    />

    <Prose>
      <p>
        Notice the message isn't asking for a phone call. It isn't asking
        the patient to log into a portal or remember a password. It links
        directly to a Stripe-hosted page that already knows the balance and
        the breakdown. The friction between "I should pay this" and "I
        paid this" is a single tap.
      </p>

      <h2>The flow, end to end</h2>
      <p>
        Every step of the workflow used to require a different system, a
        different staff member, or a different week. Now they're a single
        chain that runs the moment the claim posts.
      </p>
    </Prose>

    <FlowDiagram
      title="Text-to-pay end-to-end"
      nodes={[
        { label: "Claim posts", sublabel: "Insurance settled", icon: Activity },
        { label: "Itemized statement built", sublabel: "Line by line", icon: FileText },
        { label: "Channel + tone chosen", sublabel: "From patient history", icon: MessageSquare },
        { label: "Staff preview", sublabel: "Quick approval", icon: Eye },
        { label: "Send", sublabel: "SMS or email", icon: Send },
        { label: "Stripe payment", sublabel: "<60s", icon: CreditCard },
      ]}
    />

    <Prose>
      <h2>The economics, side by side</h2>
      <p>
        It is worth doing the simple math. A practice that sends 200 paper
        statements a month at $1 each is spending $2,400 a year on paper —
        and that's before the staff time spent printing, stuffing, and
        running them to the post office. The same practice on text-to-pay
        spends nothing per send and clears most balances in under a minute,
        often before they ever enter aging.
      </p>
      <p>
        That's not the whole story, though. The bigger swing is what
        happens to the share of balances that <em>don't</em> get collected.
        Mailed statements have a meaningful drop-off rate; balances that
        sit longer than 90 days drop further; eventually they become write-
        offs. Text-to-pay catches the patient when the visit is fresh and
        the bill is small — both factors that materially raise the
        likelihood of a quick resolution.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 200, label: "Statements a typical practice sends per month" },
        { value: 2400, prefix: "$", label: "Annual paper + postage cost (200/mo at $1)" },
        { value: 0, prefix: "$", label: "Per-send cost on text-to-pay" },
        { value: 90, suffix: "%+", label: "Of balances collected before AR aging" },
      ]}
    />

    <Prose>
      <h2>Why staff approve, every time</h2>
      <p>
        The staff preview step is small, but it matters. Most practices have
        learned, painfully, that automated billing messages can land in the
        wrong way — the patient who just had a bad visit, the patient
        who's mid-dispute, the patient who already paid by check yesterday.
        The preview is the human moment that keeps the system honest.
      </p>
      <p>
        Asha's job is to do the work — build the statement, draft the
        message, pick the channel, format the link. The human's job is to
        glance, approve, and move on. That tradeoff is what makes the
        system feel like a teammate instead of a robot.
      </p>
    </Prose>

    <FeatureCallout
      stat="<60s"
      label="Average time from statement send to payment received"
      description="An itemized statement, a tone-matched message, a Stripe-hosted link. Most balances clear before the patient leaves the parking lot."
      icon={Zap}
    />

    <Prose>
      <h2>Where the practice ends up</h2>
      <p>
        A practice that fully runs on text-to-pay has a different-shaped AR
        report. The 0–30 column is small, because most balances don't sit
        there. The 30–60, 60–90, and 90+ columns are smaller still, because
        the balances that would have aged into them already cleared. The
        end-of-month collections push, the calls to chase old balances, the
        slow attrition of write-offs — all reduced not by a heroic
        collections team, but by a workflow that doesn't manufacture aging
        in the first place.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Paper statements were the right tool for an era when reaching a
        patient meant printing a thing and putting it in a mailbox. That
        era is over. Text-to-pay turns billing into a moment, not a cycle —
        and the moment is short enough that most balances close before the
        first envelope would have left the printer.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogTextToPayInstantStatements;
